/** Event fact rows (OD5-3): one label and unit per `EventFactKey`. Units come from the key's suffix (Usd → whole dollars, Rate → decimal). The card's facts are first-order at the fired month, so a KPI-backed row says "after this card" (the strip's caption names the month and the basis once) and never shows as the cockpit's own measure. */
import { EVENT_GATED_FACT_KEYS, type EventCardFact } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { kpiLabel } from "../format/labels";
import { formatPointsWord, formatUsdB, roundedToSum } from "../format";

type FactKey = EventCardFact["key"];

/** A fact that reads a KPI takes that KPI's participant label (never a bare "coverage"), at the point the card prices it: after this card. */
const KPI_OF = {
  lcrLiteAfterRate: "lcrLite", uninsuredShareAfterRate: "uninsuredShare", creToTrbcAfterRate: "creToTrbc", constructionToTrbcAfterRate: "constructionToTrbc",
  cet1ReportedAfterRate: "cet1Reported", cet1MarkedAfterRate: "cet1Marked",
} as const satisfies Partial<Record<FactKey, KpiId>>;

/** The rest are the card's own facts: each key's suffix names its unit (Usd → $M, Rate → %). */
const LOCAL_FACTS: Readonly<Record<Exclude<FactKey, keyof typeof KPI_OF>, string>> = {
  depositOutflowUsd: "Deposit outflow from the shock only",
  depositOutflowRate: "Outflow from the shock only, share of deposits",
  fhlbCapacityRemainingUsd: "Home Loan Bank borrowing left",
  dwCapacityRemainingUsd: "Fed discount window borrowing left",
  cashBufferAfterUsd: "Cash buffer after",
  thisYearEarningsHitUsd: "This year's earnings hit",
  chargeOffUsd: "Charge-off",
  provisionBuildUsd: "Extra provision",
  opexHitUsd: "Operating cost hit",
  realizedLossUsd: "Loss on sale",
  niiLossUsd: "Lost interest income",
  ciGrowthVsTrendRate: "C&I growth above the market over the last 24 months",
};

/** A cost fact that comes out negative is a gain: a provision build is a release, an earnings hit an earnings gain. The amount shows positive under its own name. */
const GAIN_LABELS: Partial<Record<FactKey, string>> = { provisionBuildUsd: "Provision release", realizedLossUsd: "Gain on sale", thisYearEarningsHitUsd: "This year's earnings gain", niiLossUsd: "Added interest income" };
export const releasedFact = (key: FactKey, value: number): boolean => GAIN_LABELS[key] !== undefined && value < 0;

/** The KPI a fact reads, when it reads one: what a limit applies to. */
export const kpiOfFact = (key: FactKey): KpiId | undefined => (KPI_OF as Partial<Record<FactKey, KpiId>>)[key];

export const factLabel = (key: FactKey, value?: number): string => {
  if (value !== undefined && releasedFact(key, value)) return GAIN_LABELS[key] ?? "";
  const kpi = kpiOfFact(key);
  return kpi === undefined ? LOCAL_FACTS[key as keyof typeof LOCAL_FACTS] : `${kpiLabel(kpi)}, after this card`;
};

/** Money in $M, ratios in % (2 dp for CET1, as everywhere else). */
export const formatFactValue = (key: FactKey, value: number): string =>
  (key === "fhlbCapacityRemainingUsd" || key === "dwCapacityRemainingUsd") && value >= 1e9 ? formatUsdB(value) :
  key === "ciGrowthVsTrendRate" ? formatPointsWord(value, { signed: true }) :
  releasedFact(key, value) ? formatMetric(-value, "usd") : key.endsWith("Usd") ? formatMetric(value, "usd") : formatMetric(value, "rate", { precision: key.startsWith("cet1") ? 2 : 1 });

/** Gated by the server until the forecast is committed; the client also masks CET1 keys while `forecastState` is 'none'. */
export const isLocked = (fact: EventCardFact, forecastCommitted: boolean): boolean =>
  fact.status === "gated" || (!forecastCommitted && (EVENT_GATED_FACT_KEYS as readonly string[]).includes(fact.key));

/** The four sources of "this year's earnings hit" (12.4 E1): provision, operating cost, loss on sale, and interest income given up (or gained, when negative), including what a forced lever does. The engine serves all four with the hit. */
export const HIT_SOURCES = ["realizedLossUsd", "niiLossUsd", "opexHitUsd", "provisionBuildUsd"] as const satisfies readonly FactKey[];
/** The step the hit prints in ($0.1M): the parts are rounded to it so the printed parts add to the printed hit. */
const HIT_STEP = 1e5;

/**
 * Names where an earnings hit or gain comes from, out of the facts the same card shows: "Includes loss on sale $17.4M, less provision release $2.5M".
 * The sources are exhaustive (the headline is their sum), each printed to the step the headline prints in with the rounding left over given to the largest, so the printed parts add to the printed hit.
 * Null when the card shows no non-zero source (or gates them), so the row never claims a source the card does not state.
 */
export function earningsHitSources(facts: readonly EventCardFact[]): string | null {
  const present = HIT_SOURCES.flatMap((key) => { const fact = facts.find((f) => f.key === key); return fact !== undefined && fact.status === "shown" ? [{ key, value: fact.value }] : []; });
  const hit = facts.find((f) => f.key === "thisYearEarningsHitUsd");
  const printed = hit !== undefined && hit.status === "shown" ? roundedToSum(present.map((p) => p.value), present.reduce((t, p) => t + p.value, 0), HIT_STEP) : present.map((p) => p.value);
  // A card stored before the sources were served states a hit its facts do not add up to: it names nothing rather than claim a complete list.
  if (hit !== undefined && hit.status === "shown" && Math.abs(present.reduce((t, p) => t + p.value, 0) - hit.value) > HIT_STEP / 2) return null;
  const parts = present.flatMap((p, i) => {
    const value = printed[i] ?? p.value;
    if (Math.abs(value) < HIT_STEP / 2) return [];
    return [{ text: `${factLabel(p.key, value).toLowerCase()} ${formatFactValue(p.key, value)}`, relief: releasedFact(p.key, value) }];
  });
  const costs = parts.filter((p) => !p.relief).map((p) => p.text).join(" and ");
  const relief = parts.filter((p) => p.relief).map((p) => p.text).join(" and ");
  if (costs === "" && relief === "") return null;
  return `Includes ${costs === "" ? relief : relief === "" ? costs : `${costs}, less ${relief}`}`;
}

/** The card's earnings hit when exactly one of its own facts makes it up and they print alike ("Extra provision $3.3M" and no separate hit): that fact's key; else null. */
export function singleSourceHit(facts: readonly EventCardFact[]): FactKey | null {
  const hit = facts.find((f) => f.key === "thisYearEarningsHitUsd");
  if (hit === undefined || hit.status !== "shown") return null;
  const sources = HIT_SOURCES.flatMap((key) => { const f = facts.find((x) => x.key === key); return f !== undefined && f.status === "shown" && f.value !== 0 ? [f] : []; });
  const only = sources[0];
  return sources.length === 1 && only !== undefined && formatFactValue(only.key, only.value) === formatFactValue(hit.key, hit.value) && (only.value < 0) === (hit.value < 0) ? only.key : null;
}
